import { extractLocalizedText } from '../../../packages/@treams/treams-analytics/src/helpers';
import {
	type RewardChange,
	type RewardPosition,
	type RewardTitle,
	type RewardUser,
	currentPositions,
	currentSalaries,
	isRewardEmployee,
	personName,
} from './buildRewardFunctions';

export const GENDER_GAP_GENDERS = ['MALE', 'FEMALE', 'OTHER'] as const;

export type GenderGapGender = (typeof GENDER_GAP_GENDERS)[number];

export type GenderGapBar = {
	gender: GenderGapGender;
	/** Average full-time salary. Absent when nobody of this gender holds the function. */
	average?: number;
	count: number;
};

export type GenderGapEmployee = {
	id: string;
	name: string;
	gender: GenderGapGender;
	/** Full-time salary. */
	salary: number;
};

export type GenderGapRow = {
	id: string;
	name: string;
	bars: GenderGapBar[];
	employees: GenderGapEmployee[];
};

export type GenderGap = {
	functions: GenderGapRow[];
	total: GenderGapRow;
};

const isGenderGapGender = (gender: unknown): gender is GenderGapGender =>
	GENDER_GAP_GENDERS.includes(gender as GenderGapGender);

function rowFor(
	id: string,
	name: string,
	employees: GenderGapEmployee[],
	language: string,
): GenderGapRow {
	return {
		id,
		name,
		bars: GENDER_GAP_GENDERS.map((gender) => {
			const amounts = employees
				.filter((employee) => employee.gender === gender)
				.map((employee) => employee.salary);

			return {
				gender,
				average:
					amounts.length === 0
						? undefined
						: amounts.reduce((sum, amount) => sum + amount, 0) / amounts.length,
				count: amounts.length,
			};
		}),
		employees: [...employees].sort((left, right) => left.name.localeCompare(right.name, language)),
	};
}

// Average full-time salary per gender for each function and for everyone.
// People who prefer not to say, or have no gender or salary, are left out.
// Undefined when nobody counts, e.g. in a manager snapshot without gender.
export function buildGenderGap({
	users,
	positions,
	titles,
	changes,
	now,
	language,
	noFunctionName,
	totalName,
	viewerID,
}: {
	users: RewardUser[];
	positions: RewardPosition[];
	titles: RewardTitle[];
	changes: RewardChange[];
	now: Date;
	language: string;
	noFunctionName: string;
	totalName: string;
	viewerID?: string;
}): GenderGap | undefined {
	const titleNames = new Map(
		titles.map((title) => [title.jobTitleID, extractLocalizedText(title.title, language)]),
	);
	const positionByUser = currentPositions(positions, now);
	const salaryByUser = currentSalaries(changes);
	const byFunction = new Map<string, GenderGapEmployee[]>();
	const total: GenderGapEmployee[] = [];

	for (const user of users) {
		const salary = salaryByUser.get(user.userID);

		if (!salary || !isGenderGapGender(user.gender) || !isRewardEmployee(user, now, viewerID)) {
			continue;
		}

		const jobTitleID = positionByUser.get(user.userID)?.jobTitleID;
		const functionID = jobTitleID && titleNames.has(jobTitleID) ? jobTitleID : '';
		const employee: GenderGapEmployee = {
			id: user.userID,
			name: personName(user) || user.userID,
			gender: user.gender,
			salary: salary.amount,
		};

		byFunction.set(functionID, [...(byFunction.get(functionID) ?? []), employee]);
		total.push(employee);
	}

	if (total.length === 0) {
		return undefined;
	}

	const functions = [...byFunction.entries()]
		.map(([id, employees]) =>
			rowFor(id || 'none', (id && titleNames.get(id)) || noFunctionName, employees, language),
		)
		.sort((left, right) => {
			if (left.id === 'none') return 1;
			if (right.id === 'none') return -1;

			return left.name.localeCompare(right.name, language);
		});

	return { functions, total: rowFor('total', totalName, total, language) };
}
